import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled from "styled-components";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import Grid from "src/library/components/Hedron";
import { Table } from "src/core/dashboard/components/widgets/index";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { getOrderTableColumns } from "src/core/shared/components/tables/ordersTableColumns";
import {
  ListOrdersByDropParams,
  ordersApi,
  OrdersResponse,
  IOrder,
  OrderOrigin
} from "src/core/orders/api/orders.api";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext/CreateOrderContext";
import useOrder from "src/core/orders/hooks/useOrder";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";

interface UrlParams {
  dropId: string;
  customerId: string;
}

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

const DropOrdersPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const [ { isCreatingOrder }, { createOrder } ] = useOrder();
  const TableRef = React.useRef<TableRefProps>(null);

  const {
    history, terminology, match
  } = props;

  const pricesVisible = usePriceVisibility();
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;

  const [
    {
      isLoading: isLoadingDrop, dropIdMemo, drop
    }
  ] = useDrop(dropId);

  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse>("LIST_DROP_ORDERS");

  const {
    orderStatuses, role, erp, installedModules, orderVariants
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || [],
    orderVariants: store.global.settings?.orderVariants
  }));

  const ordersData = React.useMemo(() => ordersResponse.data?.orders.result || [], [ ordersResponse.data ]);

  const onFetchData = React.useCallback(
    (params: { filters: FilterParams<IOrder>; pagination: PaginatedParams<IOrder>;}) => {
      const { orderStatus, ...filters } = params.filters;

      if (dropIdMemo) {
        getOrders<ListOrdersByDropParams>({
          pagination: params.pagination,
          filters: {
            ...filters,
            orderStatusId: orderStatus ? parseInt(orderStatus, 10) : undefined
          },
          dropId: dropIdMemo
        });
      }
    }, [ dropIdMemo ]);

  const isLoading = ordersResponse.pending;

  const reload = React.useCallback(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ TableRef ]);

  const tableColumns = React.useMemo(() => {
    if (!dropIdMemo || !erp || !installedModules || !orderStatuses || !pricesVisible || !role || !terminology) {
      return [];
    }

    return getOrderTableColumns({
      installedModules,
      terminology,
      orderStatuses,
      pricesVisible,
      actionButton: info => {
        if (dropIdMemo && erp && role) {
          return (
            <OrderActionsList
              erp={erp}
              terminology={terminology}
              role={role}
              order={info.row.original}
              reload={reload}
            />
          );
        }

        return null;
      },
      possibleOrderVariants: orderVariants
    });
  }, [
    dropIdMemo,
    erp,
    installedModules,
    orderStatuses,
    pricesVisible,
    role,
    terminology,
    reload,
    orderVariants
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <OrderCreationWrapper>
          <CreateOrderContext
            direction="left"
            key={0}
            drop={drop}
            loading={isCreatingOrder}
            createDraftOrder={(deliveryDate, options) => createOrder({
              date: deliveryDate,
              origin: OrderOrigin.Manual,
              orderVariantId: options?.orderVariant?.id
            })}
          />
        </OrderCreationWrapper>

        {drop && (
          <Table
            tableOf={terminology.order.plural.text}
            className="large"
            data={ordersData}
            onFetchData={onFetchData}
            defaultPagination={defaultPagination}
            loading={isLoading}
            pageCount={ordersResponse.data?.orders.pages || 0}
            rowCount={ordersResponse.data?.orders.count || 0}
            error={ordersResponse.error}
            columns={tableColumns}
            ref={TableRef}
            mainAction={row => history.push(`/orders/${row.original.id}`)}
            pageSizeOptions={[
              10,
              20,
              50,
              100
            ]}
          />
        )}

      </MainContentWrapper>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const OrderCreationWrapper = styled(Grid.Bounds)`
  position: relative;
  padding: ${({ theme }) => theme.spacing.padding.md} 0;
`;

export default DropOrdersPage;
